import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { GALLERY, GALLERY_CATS } from "@/data/content";

const Galleria = () => {
  const [cat, setCat] = useState("Tutti");
  const [lightbox, setLightbox] = useState(null);
  const items = cat === "Tutti" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

  return (
    <section id="galleria" data-testid="galleria-section" className="py-24 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          chapter="03"
          eyebrow="Galleria"
          title="Scorci di vita quotidiana"
          text="Camere, giardini, sale comuni e momenti di festa: un assaggio dell'atmosfera che si respira ogni giorno."
          testid="galleria-heading"
          hint="gallery-scroll-hint"
        />

        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="gallery-filters">
          {GALLERY_CATS.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => { setCat(c); setLightbox(null); }}
              className={`rounded-full px-5 py-2 text-[13px] font-semibold transition-all duration-300 ${
                cat === c ? "bg-sage text-ivory shadow-lg shadow-sage/25" : "bg-card text-moss hover:bg-sage-light hover:text-sage"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <div className="flex touch-pan-x snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:touch-auto lg:grid lg:snap-none lg:grid-cols-2 lg:gap-6 lg:overflow-visible lg:pb-0" data-testid="gallery-carousel">
            {items.map((g, i) => (
              <button key={g.label} onClick={() => setLightbox(i)} data-testid={`gallery-thumb-${i}`} className="group w-[85%] shrink-0 snap-start text-left sm:w-[60%] lg:w-full">
                <span className="block overflow-hidden rounded-3xl">
                  <img src={g.src} alt={g.label} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </span>
                <span className="mt-2.5 block text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">{g.cat}</span>
                <span className="mt-0.5 block font-serif text-lg italic text-ink transition-colors group-hover:text-sage">{g.label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence>
          {lightbox !== null && items[lightbox] && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/95 p-6 backdrop-blur-sm"
              data-testid="gallery-lightbox"
              onClick={() => setLightbox(null)}
            >
              <button data-testid="lightbox-close" aria-label="Chiudi" onClick={() => setLightbox(null)} className="absolute right-5 top-5 rounded-full bg-ivory/10 p-3 text-ivory transition-colors hover:bg-ivory/25">
                <X size={20} />
              </button>
              <button
                data-testid="lightbox-prev"
                aria-label="Foto precedente"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + items.length - 1) % items.length); }}
                className="absolute left-3 rounded-full bg-ivory/10 p-3 text-ivory transition-colors hover:bg-ivory/25 sm:left-6"
              >
                <ChevronLeft size={22} />
              </button>
              <motion.div
                key={items[lightbox].label}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="max-h-[82vh] max-w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img src={items[lightbox].src} alt={items[lightbox].label} className="max-h-[74vh] max-w-full rounded-2xl object-contain" data-testid="lightbox-image" />
                <p className="mt-3 text-center font-serif text-lg italic text-ivory/85" data-testid="lightbox-caption">{items[lightbox].label}</p>
              </motion.div>
              <button
                data-testid="lightbox-next"
                aria-label="Foto successiva"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % items.length); }}
                className="absolute right-3 rounded-full bg-ivory/10 p-3 text-ivory transition-colors hover:bg-ivory/25 sm:right-6"
              >
                <ChevronRight size={22} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Galleria;
